import { authzTransport } from "@/src/config/authzConfig.js";

export default {
  async fetchRoles() {
    try {
      const response = await authzTransport.get("/v1/roles");
      return response.data;
    } catch (error) {
      console.error("Error fetching roles from Authz service:", error);
      throw error;
    }
  },

  async fetchRole(roleId) {
    try {
      const response = await authzTransport.get(`/v1/roles/${roleId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching role ${roleId} from Authz service:`, error);
      throw error;
    }
  },

  async createRole(payload) {
    try {
      const response = await authzTransport.post("/v1/roles", payload);
      return response.data;
    } catch (error) {
      console.error("Error creating role in Authz service:", error);
      throw error;
    }
  },

  async updateRole(roleId, payload) {
    try {
      const response = await authzTransport.put(`/v1/roles/${roleId}`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error updating role ${roleId} in Authz service:`, error);
      throw error;
    }
  },

  async deleteRole(roleId) {
    try {
      const response = await authzTransport.delete(`/v1/roles/${roleId}`);
      // Return full response to access status code (204 for successful delete)
      return { status: response.status, data: response.data };
    } catch (error) {
      console.error(`Error deleting role ${roleId} in Authz service:`, error);
      throw error;
    }
  },

  async fetchNamespaces() {
    try {
      const response = await authzTransport.get("/v1/namespaces");
      return response.data;
    } catch (error) {
      console.error("Error fetching namespaces from Authz service:", error);
      throw error;
    }
  },

  async fetchNamespaceResources(namespaceId) {
    try {
      const response = await authzTransport.get(
        `/v1/namespaces/${namespaceId}/resources`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching resources for namespace ${namespaceId} from Authz service:`,
        error,
      );
      throw error;
    }
  },

  async fetchAllResources() {
    try {
      const response = await authzTransport.get(`/v1/resources`);
      return response.data;
    } catch (error) {
      console.error("Error fetching all resources from Authz service:", error);
      throw error;
    }
  },

  async updateResource(resourceId, payload) {
    try {
      const response = await authzTransport.patch(
        `/v1/resources/${resourceId}`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error updating resource ${resourceId} in Authz service:`,
        error,
      );
      throw error;
    }
  },

  async fetchPolicies() {
    try {
      const response = await authzTransport.get("/v1/policies");
      return response.data;
    } catch (error) {
      console.error("Error fetching policies from Authz service:", error);
      throw error;
    }
  },

  async fetchPoliciesByRole(roleId) {
    try {
      const response = await authzTransport.get(
        `/v1/policies?role_id=${roleId}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching policies for role ${roleId} from Authz service:`,
        error,
      );
      throw error;
    }
  },

  async createPolicy(payload) {
    try {
      const response = await authzTransport.post("/v1/policies", payload);
      return response.data;
    } catch (error) {
      console.error("Error creating policy in Authz service:", error);
      throw error;
    }
  },

  async updatePolicy(policyId, payload) {
    try {
      const response = await authzTransport.put(
        `/v1/policies/${policyId}`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error updating policy ${policyId} in Authz service:`,
        error,
      );
      throw error;
    }
  },

  async deletePolicy(policyId) {
    try {
      const response = await authzTransport.delete(`/v1/policies/${policyId}`);
      return { status: response.status, data: response.data };
    } catch (error) {
      console.error(
        `Error deleting policy ${policyId} in Authz service:`,
        error,
      );
      throw error;
    }
  },

  async batchCreatePolicies(policies) {
    try {
      const response = await authzTransport.post("/v1/policies/batch", {
        policies,
      });
      return response.data;
    } catch (error) {
      console.error("Error batch creating policies in Authz service:", error);
      throw error;
    }
  },

  async batchDeletePolicies(policyIds) {
    try {
      const response = await authzTransport.delete("/v1/policies/batch", {
        data: { policy_ids: policyIds },
      });
      return response.data;
    } catch (error) {
      console.error("Error batch deleting policies in Authz service:", error);
      throw error;
    }
  },

  async createPoliciesBatch(policies) {
    try {
      const promises = policies.map((policy) => this.createPolicy(policy));
      const results = await Promise.allSettled(promises);

      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0) {
        console.error(`${failed.length} policy creations failed:`, failed);
      }

      return {
        total: policies.length,
        succeeded: results.filter((r) => r.status === "fulfilled").length,
        failed: failed.length,
      };
    } catch (error) {
      console.error("Error batch creating policies:", error);
      throw error;
    }
  },

  async upsertPoliciesBatch(policies, existingPolicies = []) {
    try {
      const promises = [];

      policies.forEach((policy) => {
        const existingPolicy = existingPolicies.find(
          (ep) =>
            ep.attributes.role_id === policy.role_id &&
            ep.attributes.resource_id === policy.resource_id &&
            ep.attributes?.action?.toUpperCase() ===
              policy?.action?.toUpperCase(),
        );

        if (existingPolicy) {
          promises.push(this.updatePolicy(existingPolicy.id, policy));
        } else {
          promises.push(this.createPolicy(policy));
        }
      });

      const results = await Promise.allSettled(promises);

      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0) {
        console.error(`${failed.length} policy operations failed:`, failed);
      }

      return {
        total: policies.length,
        succeeded: results.filter((r) => r.status === "fulfilled").length,
        failed: failed.length,
      };
    } catch (error) {
      console.error("Error batch upserting policies:", error);
      throw error;
    }
  },

  async fetchRoleMemberships() {
    try {
      const response = await authzTransport.get("/v1/role-memberships");
      return response.data;
    } catch (error) {
      console.error("Error fetching role memberships:", error);
      throw error;
    }
  },

  async getUserRoleMembership(userId) {
    try {
      const response = await authzTransport.get(
        `/v1/role-memberships/user/${userId}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching role membership for user ${userId}:`,
        error,
      );
      throw error;
    }
  },

  async updateRoleMembership(membershipId, payload) {
    try {
      const response = await authzTransport.put(
        `/v1/role-memberships/${membershipId}`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating role membership ${membershipId}:`, error);
      throw error;
    }
  },

  async updateRoleMembershipByUser(userId, payload) {
    try {
      const response = await authzTransport.put(
        `/v1/role-memberships/user/${userId}`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error updating role membership for user ${userId}:`,
        error,
      );
      throw error;
    }
  },

  async createRoleMembership(payload) {
    try {
      const response = await authzTransport.post(
        `/v1/role-memberships`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("Error creating role membership:", error);
      throw error;
    }
  },

  async deleteRoleMembership(membershipId) {
    try {
      const response = await authzTransport.delete(
        `/v1/role-memberships/${membershipId}`,
      );
      return { status: response.status, data: response.data };
    } catch (error) {
      console.error(`Error deleting role membership ${membershipId}:`, error);
      throw error;
    }
  },
};
